import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Typography from '@mui/material/Typography';
import {Alert, AlertColor, Button, Collapse, DialogActions, IconButton, styled} from '@mui/material';
import {Close, CloudUploadRounded} from '@mui/icons-material';
import {useState} from 'react';

import {AxiosProgressEvent} from 'axios';
import {apiClient} from '../../../../foundation/apiClient';
import LinearProgressIndicator from '../LinearProgressIndicator';
import {useEventBatching} from '../../../../api/events';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

interface UploadModalProps {
    open: boolean;
    setOpen: (newState: boolean) => void;
    sectionId: string;
}

export const BackgroundUploadModal = (props: UploadModalProps) => {
    const [alertMessage, updateAlertMessage] = useState<string | undefined>(undefined);
    const [openAlert, setAlertOpen] = useState<boolean>(false);
    const [alertInfoSeverity, updateAlertInfoSeverity] = useState<AlertColor>('error');
    const [imageFile, uploadImageFile] = useState<File | undefined>(undefined);
    const [uploadProgress, updateUploadProgress] = useState<number | undefined>(undefined);
    const {trackEvent} = useEventBatching();

    const handleClose = () => {
        props.setOpen(false);
        setAlertOpen(false);
        updateAlertMessage(undefined);
        uploadImageFile(undefined);
        updateUploadProgress(undefined);
        updateAlertInfoSeverity('error');
    };

    const processImageFile = async () => {
        if (imageFile) {
            const formData = new FormData();
            formData.append('file', imageFile);
            formData.append('sectionId', props.sectionId);
            formData.append('backgroundType', 'BackgroundImage');
            await apiClient
                .post('media/background/update', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    onUploadProgress(progressEvent: AxiosProgressEvent) {
                        if (progressEvent.total) {
                            const percentage = (progressEvent.loaded / progressEvent.total) * 100;
                            updateUploadProgress(percentage);
                        }
                    },
                })

                .then(() => {
                    trackEvent({
                        eventName: 'BACKGROUND_IMG_UPLOAD_MODAL_SUCCESS',
                        props: {
                            fileName: imageFile.name,
                            sectionId: props.sectionId,
                            size: imageFile.size.toString(),
                            type: imageFile.type,
                        },
                    });
                    updateAlertMessage('Image uploaded successfully');
                    updateAlertInfoSeverity('success');
                    setAlertOpen(true);
                })
                .catch((error) => {
                    console.info(error);
                    updateAlertMessage('Something went wrong while uploading');
                    trackEvent({
                        eventName: 'BACKGROUND_IMG_UPLOAD_MODAL_FAILURE',
                        props: {
                            fileName: imageFile.name,
                            sectionId: props.sectionId,
                            size: imageFile.size.toString(),
                            type: imageFile.type,
                            error: error.toString(),
                        },
                    });
                    updateAlertInfoSeverity('error');
                    setAlertOpen(true);
                });
        } else {
            updateAlertMessage('No image file selected');
            setAlertOpen(true);
        }
    };

    return (
        <div>
            <Modal
                aria-labelledby='transition-modal-title'
                aria-describedby='transition-modal-description'
                open={props.open}
                onClose={handleClose}
                closeAfterTransition
                slots={{backdrop: Backdrop}}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}>
                <Fade in={props.open}>
                    <Box
                        sx={{
                            position: 'absolute' as 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 400,
                            bgcolor: 'white',
                            boxShadow: 24,
                            borderRadius: 2,
                            paddingLeft: 4,
                            paddingRight: 4,
                            paddingTop: 4,
                            paddingBottom: 2,
                        }}>
                        <Typography id='transition-modal-title' variant='h6' component='h2'>
                            Upload image
                        </Typography>
                        <Typography id='transition-modal-description' sx={{mt: 2}}>
                            Select an image to set as background.
                        </Typography>
                        <br />
                        <center>
                            {uploadProgress !== undefined ? (
                                <LinearProgressIndicator progress={uploadProgress} />
                            ) : (
                                <>
                                    {imageFile ? (
                                        <img height={100} width={100} src={URL.createObjectURL(imageFile)} />
                                    ) : null}
                                    <br />
                                    <br />
                                    <Button
                                        component='label'
                                        role={undefined}
                                        variant='contained'
                                        tabIndex={-1}
                                        startIcon={<CloudUploadRounded />}>
                                        Upload files
                                        <VisuallyHiddenInput
                                            type='file'
                                            accept='.jpg,.jpeg,.png'
                                            onChange={(event) => {
                                                if (event.target.files !== null && event.target.files?.length > 0) {
                                                    const newImageFile = event.target.files[0];
                                                    uploadImageFile(newImageFile);
                                                } else {
                                                    updateAlertMessage('Please select a valid file');
                                                    setAlertOpen(true);
                                                }
                                            }}
                                            multiple={false}
                                        />
                                    </Button>
                                </>
                            )}
                        </center>
                        <br />
                        <DialogActions>
                            <Button onClick={processImageFile}>Save changes</Button>
                            <Button onClick={handleClose}>Dismiss</Button>
                        </DialogActions>
                        <Collapse in={openAlert}>
                            <Alert
                                severity={alertInfoSeverity}
                                action={
                                    <IconButton
                                        aria-label='close'
                                        color='inherit'
                                        size='small'
                                        onClick={() => {
                                            setAlertOpen(false);
                                            updateAlertMessage(undefined);
                                        }}>
                                        <Close fontSize='inherit' />
                                    </IconButton>
                                }
                                sx={{mb: 2}}>
                                {alertMessage}
                            </Alert>
                        </Collapse>
                    </Box>
                </Fade>
            </Modal>
        </div>
    );
};
